// Client-side onboarding permission mirror (Module 02 §7).
//
// The server is the ONLY authorisation boundary: every wizard mutation is
// authorised API-side (`@RequiresPermission("onboarding.*")`), blocking-item
// waives and go-live are additionally re-asserted against the group_admin role
// in the service layer, and money/PII columns are redacted in the serializer.
// This hook exists so the UI does not OVER-SHOW controls a role cannot use —
// install pack, edit terminology, commit the plant map, waive, go live. A
// hidden control is a convenience, never a security guarantee.
//
// It derives the caller's effective `onboarding.*` codes from their role codes
// (`GET /v1/auth/tenants` — the only caller-role signal the client has),
// mirroring the authoritative grants in `packages/contracts/src/permissions.ts`
// (`systemRoleGrants`, the onboarding.* slice, D-034). Custom (non-system) roles
// cannot be resolved from a role code alone, so they contribute no client-side
// grants and rely on the server gate: graceful degradation, never a false allow.
//
// `has()` is FAIL-CLOSED: false until the role feed settles and false if it
// fails.

import {
  derivePermissions,
  hasLiteralRole,
  roleSignalOf,
  useRoleCodesFeed,
  type AreaPermissions,
  type RoleInput,
  type RoleSignal,
} from "../../../lib/role-codes";

/** The 14 `onboarding.*` permission codes (spec 02 §7 / D-034). */
export const ONBOARDING_PERMISSIONS = [
  "onboarding.view",
  "onboarding.manage_wizard",
  "onboarding.install_pack",
  "onboarding.edit_terminology",
  "onboarding.import.catalogue",
  "onboarding.import.customers",
  "onboarding.import.suppliers",
  "onboarding.import.opening_stock",
  "onboarding.import.machines",
  "onboarding.import.people",
  "onboarding.generate_qr_kit",
  "onboarding.checklist.waive",
  "onboarding.checklist.manual_set",
  "onboarding.go_live",
] as const;

export type OnboardingPermission = (typeof ONBOARDING_PERMISSIONS)[number];

/**
 * Role code → `onboarding.*` codes: the onboarding slice of the contracts
 * `systemRoleGrants` table (spec 02 §7). It MUST stay in step with that table —
 * the server enforces the real grants; this only governs what the UI offers.
 *
 * Notes that are easy to get wrong (all straight from §7):
 * - group_admin holds every code (it is `nonPlatformCodes` by construction).
 * - facility_admin holds everything EXCEPT `install_pack`, `edit_terminology`
 *   and `go_live`; its waive grant is limited to NON-BLOCKING items, which is a
 *   service-layer role check, not a distinct code (see `canWaive` below).
 * - `floor_worker` has NO onboarding access at all — not even `view`.
 * - finance's ✔ on "opening_stock (commit — posts value)" is a CO-permission
 *   (the committer must hold the finance role), not a distinct grant, so
 *   finance carries only `onboarding.view` here.
 */
export const ONBOARDING_ROLE_GRANTS: Readonly<Record<string, readonly OnboardingPermission[]>> = {
  group_admin: [...ONBOARDING_PERMISSIONS],
  facility_admin: [
    "onboarding.view",
    "onboarding.manage_wizard",
    "onboarding.import.catalogue",
    "onboarding.import.customers",
    "onboarding.import.suppliers",
    "onboarding.import.opening_stock",
    "onboarding.import.machines",
    "onboarding.import.people",
    "onboarding.generate_qr_kit",
    "onboarding.checklist.waive",
    "onboarding.checklist.manual_set",
  ],
  planner: ["onboarding.view", "onboarding.import.catalogue"],
  sales: ["onboarding.view", "onboarding.import.customers"],
  procurement: ["onboarding.view", "onboarding.import.suppliers"],
  finance: ["onboarding.view"],
  qc: ["onboarding.view"],
  maintenance: ["onboarding.view"],
  storekeeper: ["onboarding.view", "onboarding.import.opening_stock", "onboarding.generate_qr_kit"],
  // floor_worker: no onboarding access (§7 view row is "—"). Deliberately absent.
};

/** Import kind → the `onboarding.import.*` lane that authorises it (§7). */
export const IMPORT_KIND_PERMISSION: Readonly<Record<string, OnboardingPermission>> = {
  products: "onboarding.import.catalogue",
  materials: "onboarding.import.catalogue",
  boms: "onboarding.import.catalogue",
  routings: "onboarding.import.catalogue",
  customers: "onboarding.import.customers",
  open_sales_orders: "onboarding.import.customers",
  suppliers: "onboarding.import.suppliers",
  open_purchase_orders: "onboarding.import.suppliers",
  opening_stock: "onboarding.import.opening_stock",
  machines: "onboarding.import.machines",
  people: "onboarding.import.people",
};

/**
 * The effective onboarding permission set for a role-code set (pure,
 * unit-tested). Union of every known role's grants; unknown roles add nothing.
 */
export function deriveOnboardingPermissions(roles: RoleInput): Set<string> {
  return derivePermissions(roles, ONBOARDING_ROLE_GRANTS);
}

export interface OnboardingPermissions extends AreaPermissions, RoleSignal {
  /** The caller's resolved role codes (empty until the feed settles). */
  roleCodes: ReadonlySet<string>;
  /** True when the caller holds group_admin by its LITERAL code — the §7
   * group-only gate, which `checklist.service.ts` checks literally so a
   * trimmed clone does not inherit go-live or the blocking waive (D-224
   * ruling 7). */
  isGroupAdmin: boolean;
  /**
   * §7 waive gate. Non-blocking items need `onboarding.checklist.waive`;
   * BLOCKING items additionally require group_admin (re-asserted server-side,
   * 403 `BLOCKING_WAIVE_FORBIDDEN`). Same rule governs `manual-set` on a
   * blocking item (§3.6).
   */
  canWaive: (blocking: boolean) => boolean;
  /** True when the caller may run the import lane for `kind` (§7 matrix). */
  canImport: (kind: string) => boolean;
}

/**
 * The caller's onboarding permissions. Fail-closed while the role feed is in
 * flight or failed: screens render read-only rather than flashing a control the
 * server will refuse.
 */
export function useOnboardingPermissions(): OnboardingPermissions {
  const feed = useRoleCodesFeed();
  const codes = deriveOnboardingPermissions(feed);
  // LITERAL-ROLE-CODES: `checklist.service.ts` isGroupAdmin is literal (D-224 ruling 7).
  const isGroupAdmin = hasLiteralRole(feed, ["group_admin"]);
  const has = (code: string) => codes.has(code);
  return {
    isResolved: feed.isResolved,
    has,
    ...roleSignalOf(feed),
    isGroupAdmin,
    canWaive: (blocking: boolean) =>
      has("onboarding.checklist.waive") && (!blocking || isGroupAdmin),
    canImport: (kind: string) => {
      const code = IMPORT_KIND_PERMISSION[kind];
      return code !== undefined && has(code);
    },
  };
}
